import React from "react";
import { Link } from "react-router-dom";
import { Instagram, Youtube, Linkedin } from "lucide-react";
import { useAuth } from "../../lib/auth";
import ideathonMark from "../../assets/ideathon-mark.png";

const SectionHeading = ({ children }) => (
  <div className="mb-3" data-testid="footer-heading">
    <span className="text-[0.7rem] font-medium tracking-[0.1em] uppercase text-white">{children}</span>
  </div>
);

export default function Footer() {
  const { config } = useAuth();
  const isPreLaunch = config?.status === "pre_launch";

  return (
    <footer className="border-t border-white/10 bg-[#0A0F1E]" data-testid="footer">
      <div className="max-w-[1600px] mx-auto px-5 sm:px-8 lg:px-12 py-10 md:py-14 lg:py-16 grid gap-8 md:grid-cols-3 md:gap-16 lg:gap-24 text-sm">
        {/* Brand + socials */}
        <div>
          <div className="flex items-center gap-2.5">
            <img src={ideathonMark} alt="NoobSync Innovate logo" className="h-8 w-auto shrink-0" width="30" height="32" />
            <div className="flex items-baseline gap-1">
              <span className="font-display text-lg text-white">NoobSync</span>
              <span className="font-display text-lg text-brand">Innovate</span>
            </div>
          </div>
          <p className="text-gray-500 mt-3 max-w-xs">Where Student Ideas Come to Life.</p>
          <p className="text-gray-500 mt-2 text-xs">innovate.noobsync.com</p>
          <div className="flex items-center gap-1 mt-4 -ml-2.5" data-testid="footer-socials">
            <a href="https://www.instagram.com/noobsyncinnovations/" target="_blank" rel="noopener noreferrer" aria-label="NoobSync Innovations on Instagram" className="inline-flex items-center justify-center w-11 h-11 text-gray-500 hover:text-white transition-colors" data-testid="footer-instagram"><Instagram className="w-5 h-5" strokeWidth={1.5} /></a>
            <a href="https://www.youtube.com/channel/UCgGIoplkuZzZIvg9qPmZysQ" target="_blank" rel="noopener noreferrer" aria-label="NoobSync Innovations on YouTube" className="inline-flex items-center justify-center w-11 h-11 text-gray-500 hover:text-white transition-colors" data-testid="footer-youtube"><Youtube className="w-5 h-5" strokeWidth={1.5} /></a>
            <a href="https://in.linkedin.com/company/noobsync" target="_blank" rel="noopener noreferrer" aria-label="NoobSync Innovations on LinkedIn" className="inline-flex items-center justify-center w-11 h-11 text-gray-500 hover:text-white transition-colors" data-testid="footer-linkedin"><Linkedin className="w-5 h-5" strokeWidth={1.5} /></a>
          </div>
        </div>

        {/* Platform */}
        <div>
          <SectionHeading>Platform</SectionHeading>
          <ul className="space-y-1 text-gray-400 -my-1.5 md:my-0">
            {!isPreLaunch && <li><a href="/#about" className="block py-2 md:py-1 hover:text-white transition-colors" data-testid="footer-about">About IdeaThon</a></li>}
            {!isPreLaunch && <li><a href="/#how"   className="block py-2 md:py-1 hover:text-white transition-colors" data-testid="footer-how">How It Works</a></li>}
            {!isPreLaunch && <li><a href="/#faq"   className="block py-2 md:py-1 hover:text-white transition-colors" data-testid="footer-faq">FAQs</a></li>}
            <li><Link to="/editions" className="block py-2 md:py-1 hover:text-white transition-colors" data-testid="footer-editions">Editions</Link></li>
          </ul>
        </div>

        {/* Legal */}
        <div>
          <SectionHeading>Legal</SectionHeading>
          <ul className="space-y-1 text-gray-400 -my-1.5 md:my-0">
            <li><Link to="/terms" className="block py-2 md:py-1 hover:text-brand transition-colors" data-testid="footer-terms">Terms &amp; Conditions</Link></li>
            <li><Link to="/privacy" className="block py-2 md:py-1 hover:text-brand transition-colors" data-testid="footer-privacy">Privacy Policy</Link></li>
            <li><Link to="/refund-policy" className="block py-2 md:py-1 hover:text-brand transition-colors" data-testid="footer-refund">Cancellation &amp; Refund Policy</Link></li>
            <li><Link to="/shipping-policy" className="block py-2 md:py-1 hover:text-brand transition-colors" data-testid="footer-shipping">Shipping &amp; Delivery Policy</Link></li>
            <li><a href="https://noobsync.com/security" target="_blank" rel="noopener noreferrer" className="block py-2 md:py-1 hover:text-brand transition-colors" data-testid="footer-security">Security</a></li>
            <li><button type="button" onClick={() => window.dispatchEvent(new Event("ns-open-consent"))} className="block w-full text-left py-2 md:py-1 hover:text-brand transition-colors" data-testid="footer-cookie-settings">Cookie Settings</button></li>
            <li className="text-gray-500 py-2 md:py-1">Contact: <a href="mailto:info@noobsync.com" className="text-brand hover:text-white underline underline-offset-2 transition-colors" data-testid="footer-contact">info@noobsync.com</a></li>
          </ul>
        </div>
      </div>

      {/* Utility row */}
      <div className="border-t border-white/5">
        <div className="max-w-[1600px] mx-auto px-5 sm:px-8 lg:px-12 py-5 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <p className="text-xs text-gray-500 leading-relaxed" data-testid="footer-organiser">
            NoobSync Innovations (OPC) Private Limited<span className="hidden sm:inline"> · </span>
            <br className="sm:hidden" />
            <span className="whitespace-nowrap">CIN U62020AP2025OPC121219</span> · Andhra Pradesh, India
          </p>
          <p className="text-xs text-gray-500 shrink-0" data-testid="footer-copyright">© {new Date().getFullYear()} NoobSync Innovate. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
}
